import { useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { StatusDots } from '@/components/StatusDots/StatusDots';
import { useSessionStore } from '@/stores/session';
import { stopAllPlayback } from '@/stores/playback';
import { requestCancelPipeline } from '@/lib/pipelineCoordinator';
import styles from './BottomBar.module.css';

interface Props {
  /** Play All Favorites — moved out of the pipeline bar. */
  onPlayAll(): void;
  playAllDisabled: boolean;
  playAllActive: boolean;
  /** Upload pipeline — slot only this round, handler is a stub. */
  onUpload(): void;
  uploadDisabled?: boolean;
  /** When provided AND uploadDisabled is true, surfaces as the disabled
   *  button's `title` so the user sees the exact reason on hover instead
   *  of staring at a grey button (e.g. "Block 3 has no text" — common
   *  trap when StatusDots show all-green: dots only check fav+take, the
   *  upload gate also needs non-empty text per block). */
  uploadDisabledReason?: string | null;
  /** Asset nav. */
  currentAssetIdx: number;
  totalAssets: number;
  onPrev(): void;
  onNext(): void;
  onSummary(): void;
}

/** Sticky bottom bar for the TTS (and later STS) generation view.
 *  Groups playback + upload + asset-nav actions in a compact strip. */
export function BottomBar({
  onPlayAll,
  playAllDisabled,
  playAllActive,
  onUpload,
  uploadDisabled = false,
  uploadDisabledReason = null,
  currentAssetIdx,
  totalAssets,
  onPrev,
  onNext,
  onSummary,
}: Props) {
  const isFirst = currentAssetIdx === 0;
  const isLast = currentAssetIdx === totalAssets - 1;

  return (
    <div className={styles.bar} data-sticky-bottom-bar="1">
      <div className={styles.left}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onPlayAll}
          disabled={playAllDisabled}
        >
          {playAllActive ? (
            <>
              <i className="ph-bold ph-x" /> Cancel
            </>
          ) : (
            <>
              <i className="ph-fill ph-play" /> Play All Favorites
            </>
          )}
        </Button>
        <DurationInfo />
        {/* Per-block status dots — vanilla mount inside the bottom bar
            next to ttsDurationInfo (legacy/index.html:2458). The component
            returns null when there are no TTS blocks (STS uses a different
            state shape) so unconditional mount here is safe. */}
        <StatusDots />
      </div>
      <div className={styles.right}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            // Same intent as Next / Prev navigation: silence anything
            // currently playing (chips, timeline, version-bar, generate-
            // all pipeline) the moment the modal opens, not when the
            // user actually starts the upload — prevents audio bleeding
            // through the modal interaction.
            requestCancelPipeline();
            stopAllPlayback();
            onUpload();
          }}
          disabled={uploadDisabled}
          title={
            uploadDisabled && uploadDisabledReason
              ? uploadDisabledReason
              : undefined
          }
          data-coach="upload"
        >
          <i className="ph-bold ph-upload-simple" /> Upload Version
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrev}
          disabled={isFirst}
        >
          <i className="ph-bold ph-arrow-left" /> Prev
        </Button>
        {isLast ? (
          <Button variant="ghost" size="sm" onClick={onSummary}>
            Summary <i className="ph-bold ph-arrow-right" />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onNext}>
            Next <i className="ph-bold ph-arrow-right" />
          </Button>
        )}
      </div>
    </div>
  );
}

/** Live "current / target" duration for the active asset. Reads from the
 *  session store directly so the bar can stay agnostic to which view (TTS
 *  vs STS) is mounted. Color-graded against the target duration. */
function DurationInfo() {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const customDurations = useSessionStore((s) => s.customDurations);
  // Subscribe to slices separately — `selectAllAssets` builds a fresh array
  // per call so subscribing to it directly would re-render every store tick.
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const activeAsset = useMemo(
    () => [...(configAssets ?? []), ...customAssets][currentAssetIdx],
    [configAssets, customAssets, currentAssetIdx],
  );

  const blocks = assetBlocks[currentAssetIdx] ?? [];
  let current = 0;
  let hasFav = false;
  for (const b of blocks) {
    const fav = b.favIdx >= 0 ? b.takes[b.favIdx] : null;
    if (fav && fav.duration > 0) {
      current += fav.duration;
      hasFav = true;
    }
  }

  if (!hasFav) return <span className={styles.durationInfo} />;

  const target =
    activeAsset?.targetDuration ?? customDurations[currentAssetIdx] ?? null;

  const fmt = (s: number) => `${s.toFixed(1)}s`;

  if (target == null || target <= 0) {
    return <span className={styles.durationInfo}>{fmt(current)}</span>;
  }

  let color = 'var(--color-success)';
  if (current > target * 1.1) color = 'var(--color-error)';
  else if (current > target) color = 'var(--accent-orange)';

  return (
    <span className={styles.durationInfo} style={{ color }}>
      {fmt(current)} / {fmt(target)}
    </span>
  );
}
